
'use client';

import React from 'react';
import { DemoBanner } from '@/components/DemoBanner';
import { Search, Heart, ShoppingBag, ArrowRight, Instagram, Facebook, Twitter } from 'lucide-react';
import Image from 'next/image';

export default function FashionDemo() {
  const products = [
    { name: "Klassik Ko'ylak", category: "Ayollar", price: "285 000 so'm", seed: 50 },
    { name: "Slim Fit Shim", category: "Erkaklar", price: "320 000 so'm", seed: 51 },
    { name: "Yozgi Dress", category: "Ayollar", price: "450 000 so'm", seed: 52 },
    { name: "Casual Jacket", category: "Erkaklar", price: "680 000 so'm", seed: 53 },
    { name: "Chic Blouse", category: "Ayollar", price: "195 000 so'm", seed: 54 },
    { name: "Sport Hoodie", category: "Unisex", price: "380 000 so'm", seed: 55 },
    { name: "Elegant Suit", category: "Erkaklar", price: "1 200 000 so'm", seed: 56 },
    { name: "Mini Dress", category: "Ayollar", price: "365 000 so'm", seed: 57 },
  ];

  return (
    <div className="min-h-screen bg-white pt-11 font-sans selection:bg-black selection:text-white">
      <DemoBanner />
      
      {/* TOP BANNER */}
      <div className="bg-black text-white text-center py-2 text-[10px] md:text-xs font-bold tracking-widest uppercase">
        Bepul yetkazib berish — 500 000 so'mdan yuqori buyurtmalar
      </div>

      {/* NAVIGATION */}
      <nav className="border-b border-gray-100 py-6 sticky top-11 bg-white z-40">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="text-3xl font-display italic font-bold tracking-tighter">LUXE</div>
          <div className="hidden lg:flex items-center gap-10 text-[11px] font-bold uppercase tracking-widest text-gray-400">
            <a href="#" className="text-black">Yangi Kolleksiya</a>
            <a href="#" className="hover:text-black">Ayollar</a>
            <a href="#" className="hover:text-black">Erkaklar</a>
            <a href="#" className="hover:text-black">Aksessuarlar</a>
            <a href="#" className="text-red-500">Sale</a>
          </div>
          <div className="flex items-center gap-6">
            <Search size={20} className="text-gray-400 cursor-pointer" />
            <Heart size={20} className="text-gray-400 cursor-pointer" />
            <div className="relative cursor-pointer">
              <ShoppingBag size={20} className="text-black" />
              <span className="absolute -top-2 -right-2 bg-black text-white text-[8px] w-4 h-4 rounded-full flex items-center justify-center">0</span>
            </div>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative h-[90vh] bg-[#f2f2f2] flex items-center overflow-hidden">
        <div className="absolute inset-0 opacity-40">
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120vw] h-[120vw] bg-gradient-to-br from-gray-200 to-gray-400 rounded-full" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.4em] text-gray-400 mb-6 block">Kuz / Qish 2026</span>
            <h1 className="text-7xl md:text-9xl font-display italic font-bold leading-[0.9] mb-10">2026 Bahor Kolleksiyasi</h1>
            <p className="text-lg text-gray-500 mb-12 max-w-md">Zamonaviy uslub. Mukammal sifat. Sizning har bir kuningiz uchun.</p>
            <div className="flex gap-4">
              <button className="px-10 py-5 bg-black text-white text-xs font-bold uppercase tracking-widest hover:bg-gray-800 transition-colors">Kolleksiyani Ko'rish</button>
              <button className="px-10 py-5 border border-black text-black text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors">Sale →</button>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="py-32">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-end mb-16">
            <h2 className="text-4xl font-display italic font-bold">Yangi Kelganlar</h2>
            <a href="#" className="text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-black border-b border-transparent hover:border-black pb-1 transition-all">Barchasini ko'rish</a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-16 gap-x-8">
            {products.map((p, i) => (
              <div key={i} className="group cursor-pointer">
                <div className="relative aspect-[3/4] bg-gray-50 mb-6 overflow-hidden">
                  <Image 
                    src={`https://picsum.photos/seed/${p.seed}/600/800`} 
                    alt={p.name} 
                    fill 
                    className="object-cover group-hover:scale-110 transition-transform duration-1000 grayscale group-hover:grayscale-0"
                    data-ai-hint="fashion clothes model"
                  />
                  <div className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <Heart size={16} />
                  </div>
                  <button className="absolute bottom-0 left-0 w-full py-4 bg-black text-white text-[10px] font-bold uppercase tracking-widest translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    Savatga qo'shish
                  </button>
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-tight mb-1">{p.name}</h3>
                    <p className="text-xs text-gray-400 font-medium">{p.category}</p>
                  </div>
                  <p className="text-sm font-bold">{p.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES BANNER */}
      <section className="grid md:grid-cols-3">
        {[
          { title: "Ayollar Kolleksiyasi", bg: "bg-[#e5e5e5]" },
          { title: "Erkaklar Kolleksiyasi", bg: "bg-[#d4d4d4]" },
          { title: "Aksessuarlar", bg: "bg-[#c5c5c5]" }
        ].map((cat, i) => (
          <div key={i} className={`h-[60vh] ${cat.bg} flex flex-col items-center justify-center text-center p-10 group cursor-pointer`}>
            <h3 className="text-3xl font-display italic font-bold mb-8 group-hover:scale-110 transition-transform">{cat.title}</h3>
            <button className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] border-b border-black pb-2">Ko'rish <ArrowRight size={14} /></button>
          </div>
        ))}
      </section>

      {/* FOOTER */}
      <footer className="bg-black text-white py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-16 mb-20">
            <div className="col-span-2">
              <div className="text-4xl font-display italic font-bold tracking-tighter mb-8">LUXE</div>
              <p className="text-gray-500 text-sm max-w-sm leading-relaxed mb-8">
                Zamonaviy moda olami markazi. Biz sizning uslubingizni yaratamiz va sifatni birinchi o'ringa qo'yamiz.
              </p>
              <div className="flex gap-6">
                <Instagram size={20} className="cursor-pointer hover:text-white/70" />
                <Facebook size={20} className="cursor-pointer hover:text-white/70" />
                <Twitter size={20} className="cursor-pointer hover:text-white/70" />
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest mb-8">Kompaniya</h4>
              <ul className="space-y-4 text-xs text-gray-500">
                <li><a href="#" className="hover:text-white">Biz haqimizda</a></li>
                <li><a href="#" className="hover:text-white">Do'konlar</a></li>
                <li><a href="#" className="hover:text-white">Karyera</a></li>
                <li><a href="#" className="hover:text-white">Aloqa</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest mb-8">Yordam</h4>
              <ul className="space-y-4 text-xs text-gray-500">
                <li><a href="#" className="hover:text-white">Yetkazib berish</a></li>
                <li><a href="#" className="hover:text-white">Qaytarish</a></li>
                <li><a href="#" className="hover:text-white">To'lovlar</a></li>
                <li><a href="#" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 text-[10px] text-gray-600 font-bold uppercase tracking-widest flex justify-between">
            <span>© 2026 LUXE Fashion</span>
            <div className="flex gap-8">
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
